import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  getPropertyNameWithShortAddress,
  getShortAddressFromAddress
} from "shared/addressUtils";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { Property } from "types/landlord/address";
import { Dispatch, FC, SetStateAction, useMemo } from "react";
import { Table } from "shared/app/Table";
import { SearchSM } from "untitledui-js-base";

import { getErrorMessage } from "utils/error";
import {
  Action,
  ContextMenuPopover
} from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { Button } from "shared/components";
import { Door } from "providers/Layout/components/Door";

type Props = {
  data: Property[];
  setSelectedPropertyId: Dispatch<SetStateAction<string | null>>;
  setShowDeletePropertyModal: (value: boolean) => void;
  hasSearchParams?: boolean;
  isLoading?: boolean;
};

export const PropertiesTable: FC<Props> = ({
  data,
  setShowDeletePropertyModal,
  setSelectedPropertyId,
  hasSearchParams,
  isLoading
}) => {
  const navigate = useNavigate();
  const { type } = useAuthContext();

  const columns = useMemo(() => {
    const isBoomscreenAndPms = type.isBoomScreen && type.isPMS;
    const cols = [
      {
        accessorFn: (row) => getShortAddressFromAddress(row.address),
        header: "Property",
        size: isBoomscreenAndPms ? 510 : 200,
        cell: (p) => {
          const property = p.row.original;
          return (
            <>
              <p className="text-sm font-medium text-content-1000 max-w-[460px] truncate">
                {property.name}
              </p>
              <p className="text-xs text-content-400">{p.getValue()}</p>
            </>
          );
        }
      },
      {
        accessorKey: "total_units",
        header: "Units",
        size: isBoomscreenAndPms ? 483 : 100,
        cell: (p) => {
          const title = p.getValue() > 1 ? "Units" : "Unit";
          return (
            <div className="flex gap-2">
              <Door className="shrink-0 stroke-content-500" size="20" />
              <p className="text-content-500">{p.getValue() + " " + title}</p>
            </div>
          );
        }
      }
    ];

    if (type.isPMS)
      // @ts-expect-error
      cols.push({
        accessorKey: "pms_id",
        header: "Source",
        cell: (p) => (
          <p className="font-medium text-content-1000">
            {p.getValue() ? "PMS" : "Manually added"}
          </p>
        )
      });

    cols.push({
      // @ts-expect-error -- let it be
      id: "popover-action",
      header: "",
      size: 59,
      cell: (p) => {
        const property = p.row.original;

        const popoverActions: Action[] = [
          {
            title: "Review",
            onClick: () => navigate(`/properties/${property.id}`)
          }
        ];

        if (type.isBoomScreen)
          popoverActions.push({
            title: "Copy link to property",
            onClick: async (e) => {
              e.stopPropagation();

              if (!property.total_units) return;

              try {
                await navigator.clipboard.writeText(
                  property.magic_link?.application_link
                );

                toast.info("Link copied");
              } catch (err) {
                toast.error(getErrorMessage(err));
              }
            },
            disabled: !property.total_units,
            tooltip: "You can't copy a link to a property without units"
          });

        popoverActions.push({
          title: "Delete property",
          color:
            property.can_delete && !isBoomscreenAndPms
              ? "text-danger-500"
              : "text-content-100",
          onClick: (e) => {
            e.stopPropagation();
            if (property.can_delete && !isBoomscreenAndPms) {
              setShowDeletePropertyModal(true);
              setSelectedPropertyId(property?.id);
            }
          },
          disabled: !property.can_delete || isBoomscreenAndPms,
          tooltip:
            type.isPMS && type.isBoomScreen
              ? "You can't delete a property"
              : "You can't delete a property with applications"
        });

        return <ContextMenuPopover actions={popoverActions} />;
      }
    });
    return cols;
  }, [type.isBoomScreen]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <Table
      rowHeight="64px"
      tdClassName="!py-2"
      emptyStateTitle={
        hasSearchParams ? "No results found" : "Properties will appear here"
      }
      isLoading={isLoading}
      emptyStateText={
        hasSearchParams
          ? "We couldn't find any data matching your query"
          : "Add properties to be able to invite applicants for screening"
      }
      EmptyStateIcon={hasSearchParams ? SearchSM : undefined}
      table={table}
      onRowClick={(id) => navigate(`/properties/${id}`)}
      mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
      mobileRow={(row) => (
        <div className="flex flex-col gap-2 relative">
          <p className="text-base font-bold text-content-1000">
            {row.original.name}
          </p>

          <p className="text-xs font-normal text-content-500">
            {getPropertyNameWithShortAddress(
              row.original,
              row.original?.address
            )}
          </p>

          <div className="flex justify-between items-center py-4 my-4 border-y border-alpha-40">
            <div className="flex gap-2 items-center">
              <Door className="shrink-0 stroke-content-500" size="20" />
              <p className="text-content-500 font-medium">
                {row.original.total_units}
              </p>
            </div>
            <p className="text-content-500 font-medium">
              {row.original.total_units > 1 ? "Units" : "Unit"}
            </p>
          </div>

          <Button
            disabled={!row.original.total_units}
            onClick={async (e) => {
              e.stopPropagation();

              if (!row.original.total_units) return;

              try {
                await navigator.clipboard.writeText(
                  row.original.magic_link?.application_link
                );

                toast.info("Link copied");
              } catch (err) {
                toast.error(getErrorMessage(err));
              }
            }}
          >
            Copy link to property
          </Button>

          <div className="absolute -right-4 -top-4">
            <ContextMenuPopover
              actions={[
                {
                  title: "Review",
                  onClick: () => navigate(`/properties/${row.original.id}`)
                },
                {
                  title: "Delete property",
                  color:
                    row.original.can_delete && !type.isBoomScreen
                      ? "text-danger-500"
                      : "text-content-100",
                  onClick: (e) => {
                    e.stopPropagation();
                    if (row.original.can_delete && !type.isBoomScreen) {
                      setShowDeletePropertyModal(true);
                      setSelectedPropertyId(row.original?.id);
                    }
                  },
                  disabled: !row.original.can_delete || type.isBoomScreen,
                  tooltip:
                    type.isPMS && type.isBoomScreen
                      ? "You can't delete a property"
                      : "You can't delete a property with applications"
                }
              ]}
            />
          </div>
        </div>
      )}
    />
  );
};
